import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, Flex, ChakraProvider, extendTheme } from '@chakra-ui/react';
import Header from './components/layout/Header';
import DesktopOnlyWrapper from './components/layout/DesktopOnlyWrapper';
import HomePage from './pages/HomePage';
import CampaignPage from './pages/CampaignPage';
import CreateEventWizard from './components/events/CreateEventWizard';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import SellerProfileSetupModal from './components/profile/SellerProfileSetupModal';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ReviewerInviteAcceptPage from './pages/ReviewerInviteAcceptPage';
import ReviewerCampaignPage from './pages/ReviewerCampaignPage';
import EventInviteAcceptPage from './pages/EventInviteAcceptPage';
import EventReviewPage from './pages/EventReviewPage';
import ProfilePage from './pages/ProfilePage';
import DocsPage from './pages/DocsPage';
import BillingPage from './pages/BillingPage';
import AdminPage from './pages/AdminPage';
import IntegrationsPage from './pages/IntegrationsPage';
import EmailDomainsPage from './pages/EmailDomainsPage';
import WorkspaceContextPage from './pages/WorkspaceContextPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { InsufficientCreditsProvider } from './contexts/InsufficientCreditsContext';
import { SignupModalProvider } from './contexts/SignupModalContext';
import api from './services/api';

// Custom theme with dark mode and royal blue accents
const theme = extendTheme({
  config: {
    initialColorMode: 'dark',
    useSystemColorMode: false,
  },
  colors: {
    brand: {
      50: 'rgba(65, 105, 225, 0.1)',
      100: 'rgba(65, 105, 225, 0.2)',
      200: 'rgba(65, 105, 225, 0.3)',
      300: 'rgba(65, 105, 225, 0.4)',
      400: 'rgba(65, 105, 225, 0.5)',
      500: 'rgba(65, 105, 225, 0.6)',
      600: 'rgba(65, 105, 225, 0.7)',
      700: 'rgba(65, 105, 225, 0.8)',
      800: 'rgba(65, 105, 225, 0.9)',
      900: 'rgba(65, 105, 225, 1)',
    },
  },
  styles: {
    global: {
      body: {
        bg: '#1a1a22',
        color: '#e0e0e8',
      },
      // Custom scrollbar styling
      '*::-webkit-scrollbar': {
        width: '8px',
        height: '8px',
      },
      '*::-webkit-scrollbar-track': {
        bg: '#1a1a22',
        borderRadius: '4px',
      },
      '*::-webkit-scrollbar-thumb': {
        bg: 'rgba(80, 80, 80, 0.6)',
        borderRadius: '4px',
        border: '1px solid #1a1a22',
      },
      '*::-webkit-scrollbar-thumb:hover': {
        bg: 'rgba(80, 80, 80, 0.8)',
      },
      '*::-webkit-scrollbar-corner': {
        bg: '#1a1a22',
      },
      // Firefox scrollbar styling
      '*': {
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(80, 80, 80, 0.6) #1a1a22',
      },
    },
  },
  components: {
    Button: {
      baseStyle: {
        _focus: { boxShadow: 'outline' },
      },
      variants: {
        solid: {
          bg: 'brand.700',
          _hover: { bg: 'brand.800' },
        },
        ghost: {
          _hover: { bg: 'brand.100' },
        },
      },
    },
    Menu: {
      parts: ['list', 'item'],
      baseStyle: {
        list: {
          bg: '#1a1a22',
          borderColor: 'gray.700',
        },
        item: {
          bg: '#1a1a22',
          color: '#e0e0e8',
          _hover: { bg: 'rgba(65, 105, 225, 0.1)' },
          _focus: { bg: 'rgba(65, 105, 225, 0.1)' },
        },
      },
    },
    Card: {
      baseStyle: {
        container: {
          bg: '#2A2F36',
          borderRadius: 'md',
        },
      },
    },
    Tooltip: {
      baseStyle: {
        bg: '#1e1e24',
        color: 'rgba(255,255,255,0.85)',
        borderRadius: '6px',
        fontSize: '11px',
        fontWeight: '500',
        px: '8px',
        py: '5px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
      },
      defaultProps: {
        hasArrow: false,
      },
    },
  },
});

// Protected route component with desktop-only restriction
const ProtectedRoute = ({ children, allowTrial = false }) => {
  const { user, loading } = useAuth();
  
  // Show loading state while checking authentication
  if (loading) {
    return <Box p={4} textAlign="center">Loading...</Box>;
  }
  
  // Redirect to login if no user (AuthContext handles trial mode automatically)
  if (!user) {
    return <Navigate to="/login" />;
  }
  
  // Wrap protected content in DesktopOnlyWrapper
  return (
    <DesktopOnlyWrapper>
      {children}
    </DesktopOnlyWrapper>
  );
};

function AppContent() {
  const { user, token } = useAuth();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Fetch subscription status when user is logged in
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (user && token) {
        try {
          localStorage.setItem('token', token);
          const { data } = await api.get('/api/payments/subscription-status');
          setSubscriptionStatus(data);
        } catch (error) {
          console.error('Error fetching subscription status:', error);
        }
      }
    };

    fetchSubscriptionStatus();
    
    // Listen for credit update events
    const handleCreditUpdate = () => {
      fetchSubscriptionStatus();
    };
    
    const events = ['enrichmentCompleted', 'verificationCompleted', 'emailDraftCompleted', 'customFieldCompleted', 'exportCompleted', 'subscriptionUpgraded'];
    events.forEach(event => {
      window.addEventListener(event, handleCreditUpdate);
    });
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleCreditUpdate);
      });
    };
  }, [user, token]);

  return (
    <InsufficientCreditsProvider userSubscriptionStatus={subscriptionStatus}>
    <Flex direction="column" minH="100vh" bg="#0a0a0a" color="#fafafa">
      {user && <SellerProfileSetupModal />}
      <Routes>
        {/* Public routes - login and register work on all devices */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/reviews/accept/:token" element={<ReviewerInviteAcceptPage />} />
        <Route path="/events/accept/:token" element={<EventInviteAcceptPage />} />
        
        {/* Protected routes - desktop only */}
        <Route path="/" element={
          <ProtectedRoute>
            <Header />
            <Box as="main" p={2} w="100%" maxW="100%">
              <HomePage />
            </Box>
          </ProtectedRoute>
        } />
        <Route path="/campaign/new" element={
          <ProtectedRoute>
            <Header />
            <CampaignPage />
          </ProtectedRoute>
        } />
        <Route path="/campaign/:id" element={
          <ProtectedRoute>
            <Header />
            <CampaignPage />
          </ProtectedRoute>
        } />
        <Route path="/events/new" element={
          <ProtectedRoute>
            <Header />
            <Box as="main" maxW="100%" mx="auto">
              <CreateEventWizard />
            </Box>
          </ProtectedRoute>
        } />
        <Route path="/events/:eventId/edit" element={
          <ProtectedRoute>
            <Header />
            <Box as="main" maxW="100%" mx="auto">
              <CreateEventWizard />
            </Box>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Header />
            <Box as="main" p={2} maxW="100%" mx="auto">
              <ProfilePage />
            </Box>
          </ProtectedRoute>
        } />
        <Route path="/docs" element={
          <ProtectedRoute>
            <Header />
            <DocsPage />
          </ProtectedRoute>
        } />
        <Route path="/billing" element={
          <ProtectedRoute>
            <Header />
            <Box as="main" p={2} maxW="100%" mx="auto">
              <BillingPage />
            </Box>
          </ProtectedRoute>
        } />
        <Route path="/integrations" element={
          <ProtectedRoute>
            <Header />
            <Box as="main" p={2} maxW="100%" mx="auto">
              <IntegrationsPage />
            </Box>
          </ProtectedRoute>
        } />
        <Route path="/outbound-setup" element={
          <ProtectedRoute>
            <Header />
            <Box as="main" p={2} maxW="100%" mx="auto">
              <EmailDomainsPage />
            </Box>
          </ProtectedRoute>
        } />
        <Route path="/email/domains" element={<Navigate to="/outbound-setup" replace />} />
        <Route path="/email/warming" element={<Navigate to="/outbound-setup#email-warming" replace />} />
        <Route path="/workspace" element={
          <ProtectedRoute>
            <Header />
            <Box as="main" maxW="100%" mx="auto">
              <WorkspaceContextPage />
            </Box>
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <Header />
            <Box as="main" p={2} maxW="100%" mx="auto">
              <AdminPage />
            </Box>
          </ProtectedRoute>
        } />
        <Route path="/reviews/:campaignId" element={
          <ProtectedRoute>
            <Header />
            <Box as="main" p={2} maxW="100%" mx="auto">
              <ReviewerCampaignPage />
            </Box>
          </ProtectedRoute>
        } />
        <Route path="/events/review/:eventId" element={
          <ProtectedRoute>
            <Header />
            <Box as="main" p={2} w="100%" maxW="100%">
              <EventReviewPage />
            </Box>
          </ProtectedRoute>
        } />
      </Routes>
    </Flex>
    </InsufficientCreditsProvider>
  );
}

function App() {
  return (
    <ChakraProvider theme={theme}>
      <AuthProvider>
        <SignupModalProvider>
          <AppContent />
        </SignupModalProvider>
      </AuthProvider>
    </ChakraProvider>
  );
}

export default App; 